import images from '@assets/images/index'
import useTable from '@components/Table/useTable'
import { useQuery } from '@tanstack/react-query'
import { ItemType } from 'antd/es/menu/interface'
import { CancelToken } from 'axios'
import * as Mark from 'mark.js'
import { useEffect, useState } from 'react'
import * as bi from 'react-bootstrap-icons'

import * as Api from '@suntex.fim/api-client'
import { $EntityLevel, EntityLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { Dropdown, LevelSelect, Popover } from '@suntex.fim/ui'

import styles from './Admin.module.css' // Import css modules stylesheet as styles
import { IEntityApiResponse, TableBuilder, Visible } from './Utilities'

interface IContact {
  name?: string
}

interface IHasContacts {
  contacts?: ContactArray
}

interface ContactArray extends Array<IContact> {
  matching: Array<IContact>
}

function BaseTable<TResponse extends IEntityApiResponse<TItem>, TItem>(
  props: ILevelTableProps,
  getter: (
    params: { text: string; page: number },
    cancelToken?: CancelToken
  ) => Promise<TResponse>,
  tableBuilder: TableBuilder<TItem>,
  fetchPostProcessor?: (items: TItem[]) => void
) {
  // Debounce search text
  const [debouncedSearch, setDebouncedSearch] = useState(props.searchText)
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(props.searchText), 500)
    return () => clearTimeout(timeout)
  }, [props.searchText])

  const { data = [] } = useQuery({
    queryKey: ['admin', 'entities', props.level, debouncedSearch],
    queryFn: async () => {
      const response = await getter({
        text: debouncedSearch,
        page: 0
      })
      return response.items || []
    }
  })

  // Post-process and highlight after data loads
  useEffect(() => {
    if (data.length > 0) {
      if (fetchPostProcessor) {
        fetchPostProcessor(data)
      }
      setTimeout(() => {
        const instance = new Mark('td')
        const regex = new RegExp(debouncedSearch, 'gmi')
        instance.markRegExp(regex)
      }, 100)
    }
  }, [data, debouncedSearch])

  const columns = tableBuilder.toColumnTypes()

  columns.push({
    accessorKey: 'action',
    id: 'actions',
    size: 40,
    enableSorting: false,
    header: () => <images.SettingTable />,
    cell: (info) => {
      const item = info.row.original

      const isBelowStateLevel = props.level < EntityLevel.State

      const parentEntityLevelName = isBelowStateLevel
        ? $EntityLevel.describe($EntityLevel.from(props.level + 1)).label
        : ''
      const parentEntityId = isBelowStateLevel
        ? (item[`${parentEntityLevelName.toLowerCase()}Id`] as number)
        : 0
      const parentEntityName = isBelowStateLevel
        ? (item[`${parentEntityLevelName.toLowerCase()}Name`] as string)
        : ''
      const entityLevelName = $EntityLevel.describe(props.level).label
      const entityId = (item as Record<string, unknown>).id as string
      const entityName = (item as Record<string, unknown>).name as string
      const childEntityLevelName = $EntityLevel.describe(
        $EntityLevel.from(props.level - 1)
      ).label

      const content = (
        <div className="context-menu">
          <Visible isVisible={isBelowStateLevel}>
            <div>
              <span>
                {parentEntityLevelName}: {parentEntityName}
              </span>
            </div>
          </Visible>

          <div style={{ marginBottom: '20px' }}>
            <span>
              {entityLevelName}: {entityName}
            </span>
          </div>

          {/*data-bind="visible: !isSalesRep()"*/}
          <Visible isVisible={isBelowStateLevel}>
            <a
              href={`/admin/${parentEntityLevelName.toLowerCase()}/${parentEntityId}/edit`}
            >
              <bi.PencilSquare />
              Edit this {parentEntityLevelName.toLowerCase()}
            </a>
          </Visible>

          <a href={`/admin/${entityLevelName.toLowerCase()}/${entityId}/edit`}>
            <bi.PencilFill />
            Edit this {entityLevelName.toLowerCase()}
          </a>

          <Visible isVisible={isBelowStateLevel}>
            <a href={`/${entityLevelName.toLowerCase()}/${entityId}`}>
              <bi.HouseFill />
              View admin home page for this {entityLevelName.toLowerCase()}
            </a>
          </Visible>

          {/*//data-bind="visible: $root.entityLevel > enums.EntityLevel.Team"*/}
          {/*<a href={`/admin/${childEntityLevelName.toLowerCase()}s?${entityLevelName}Id=${entityId}`}><bi.ListTask />View {childEntityLevelName.toLowerCase()}s in this {entityLevelName.toLowerCase()}</a>*/}
          <a
            href={`/admin/${entityLevelName.toLowerCase()}/${entityId}/${childEntityLevelName.toLowerCase()}s`}
          >
            <bi.PencilFill />
            View {childEntityLevelName.toLowerCase()}s in this{' '}
            {entityLevelName.toLowerCase()}
          </a>
          {/*data-bind="visible: $root.entityLevel > enums.EntityLevel.Team"*/}
        </div>
      )

      return (
        <Popover
          content={content}
          trigger="click"
          placement="left"
          arrow={false}
        >
          <images.More />
        </Popover>
      )
    }
  })

  const Table = useTable<TItem>({
    data,
    columns,
    isLoading: false
  }).Table

  return <Table />
}

function StatesTable(props: ILevelTableProps) {
  const { api } = useFramework()
  const tb = new TableBuilder<Api.Models_Api_Admin_States_State>()

  tb.addColumn((x) => x.name)
  tb.addColumn((x) => x.code)
  tb.addColumn((x) => x.userId)
  tb.addColumn((x) => x.password)

  return BaseTable<
    Api.Models_Api_Admin_States_Response,
    Api.Models_Api_Admin_States_State
  >(props, (params, ct) => api.get_Admin_States(params, ct), tb)
}

function RegionsTable(props: ILevelTableProps) {
  const { api } = useFramework()
  const tb = new TableBuilder<Api.Models_Api_Admin_Regions_Region>()

  tb.addColumn((x) => x.name)
  tb.addColumn((x) => x.stateName)
  tb.addColumn((x) => x.userId)
  tb.addColumn((x) => x.password)
  tb.addColumn((x) => x.city)
  tb.addColumn((x) => x.state)
  tb.addColumn((x) => x.bpcCode).setHeaderText('BPC')

  return BaseTable<
    Api.Models_Api_Admin_Regions_Response,
    Api.Models_Api_Admin_Regions_Region
  >(props, (params, ct) => api.get_Admin_Regions(params, ct), tb)
}

function DistrictsTable(props: ILevelTableProps) {
  const { api } = useFramework()
  const tb = new TableBuilder<Api.Models_Api_Admin_Districts_District>()

  tb.addColumn((x) => x.name)
  tb.addColumn((x) => x.regionName)
  tb.addColumn((x) => x.userId)
  tb.addColumn((x) => x.password)
  tb.addColumn((x) => x.city)
  tb.addColumn((x) => x.state)
  tb.addColumn((x) => x.bpcCode).setHeaderText('BPC')

  return BaseTable<
    Api.Models_Api_Admin_Districts_Response,
    Api.Models_Api_Admin_Districts_District
  >(props, (params, ct) => api.get_Admin_Districts(params, ct), tb)
}

function SchoolsTable(props: ILevelTableProps) {
  const { api } = useFramework()
  const tb = new TableBuilder<Api.Models_Api_Admin_Schools_School>()

  tb.addColumn((x) => x.name)
  tb.addColumn((x) => x.districtName)
  tb.addColumn((x) => x).setCell((_) => <span>Contacts go here</span>)
  //ContactsCell(item.contacts as ContactArray)
  tb.addColumn((x) => x.userId)
  tb.addColumn((x) => x.password)
  tb.addColumn((x) => x.city)
  tb.addColumn((x) => x.state)
  tb.addColumn((x) => x.bpcCode).setHeaderText('BPC')

  return BaseTable<
    Api.Models_Api_Admin_Schools_Response,
    Api.Models_Api_Admin_Schools_School
  >(
    props,
    (params, ct) => api.get_Admin_Schools(params, ct),
    tb,
    (items) => PostProcessContacts(items as any, props.searchText)
  )
}

function TeamsTable(props: ILevelTableProps) {
  const { api } = useFramework()
  const tb = new TableBuilder<Api.Models_Api_Admin_Teams_Team>()

  tb.addColumn((x) => x.name)
  tb.addColumn((x) => x.schoolName)
  tb.addColumn((x) => x.className)
  tb.addColumn((x) => x.grade).setCell((item) => (
    <NoMark>{item.grade.toGradeString()}</NoMark>
  ))
  tb.addColumn((x) => x.year)
  tb.addColumn((x) => x.poNumber)
  tb.addColumn((x) => x.orderNumber)
  tb.addColumn((x) => x.contacts).setCell((item) =>
    ContactsCell(item.contacts as ContactArray)
  )
  tb.addColumn((x) => x.userId)
  tb.addColumn((x) => x.password)

  return BaseTable<
    Api.Models_Api_Admin_Teams_Response,
    Api.Models_Api_Admin_Teams_Team
  >(
    props,
    (params, ct) => api.get_Admin_Teams(params, ct),
    tb,
    (items) => PostProcessContacts(items as any, props.searchText)
  )
}

function PlayersTable(props: ILevelTableProps) {
  const { api } = useFramework()
  const tb = new TableBuilder<Api.Models_Api_Admin_Players_Player>()

  tb.addColumn((x) => x.teamName)
  tb.addColumn((x) => x.firstName)
  tb.addColumn((x) => x.lastName)
  tb.addColumn((x) => x.userId)
  //< td > <password-mask params="password: password"></password-mask></td >
  tb.addColumn((x) => x.password)
  tb.addColumn((x) => x.altUserId)
  tb.addColumn((x) => x.altPassword)
  tb.addColumn((x) => x.idNumber)

  return BaseTable<
    Api.Models_Api_Admin_Players_Response,
    Api.Models_Api_Admin_Players_Player
  >(props, (params, ct) => api.get_Admin_Players(params, ct), tb)
}

function NoMark(props: any) {
  return <div className={styles['no-mark']}>{props.children}</div>
}

function ContactsCell(contacts: ContactArray) {
  const matching = contacts.matching
  const hasNonMatches = contacts.length - matching.length > 0

  return (
    <>
      <div>
        {matching.map((x) => (
          <div>{x.name}</div>
        ))}
      </div>
      {hasNonMatches ? (
        <NoMark>
          <i>
            {matching.length == 0 ? '' : '+'}
            {contacts.length - matching.length} non-matching{' '}
            {(contacts.length - matching.length).noun('contact', 'contacts')}
          </i>
        </NoMark>
      ) : (
        ''
      )}
    </>
  )
}

function PostProcessContacts(items: IHasContacts[], searchText: string) {
  for (const item of items) {
    ;(item.contacts as ContactArray).matching = (item.contacts ?? []).filter(
      (x) => (x.name || '').toLowerCase().includes(searchText.toLowerCase())
    )
  }
}

interface ILevelTableProps {
  level: EntityLevel
  searchText: string
}

function LevelTable(props: ILevelTableProps) {
  switch (Number(props.level)) {
    case EntityLevel.State:
      return <StatesTable {...props} />
    case EntityLevel.Region:
      return <RegionsTable {...props} />
    case EntityLevel.District:
      return <DistrictsTable {...props} />
    case EntityLevel.School:
      return <SchoolsTable {...props} />
    case EntityLevel.Team:
      return <TeamsTable {...props} />
    case EntityLevel.Player:
      return <PlayersTable {...props} />
    case EntityLevel.None:
      return <></>
    default:
      return <div>Invalid entity level {props.level}</div>
  }
}

function Admin() {
  //do this until we can secure it by verifying that the user is an admin
  //return <>Under construction</>

  const [searchText, setsearchText] = useState('')

  const [level, setLevel] = useState<EntityLevel>(EntityLevel.None)

  const entityLevels = $EntityLevel
    .describeAll()
    .filter((x) => x.value !== EntityLevel.All)
    .map((x) => ({
      key: x.value,
      label: x.value === EntityLevel.None ? 'Select a level...' : `${x.label}s`
    }))
    .reverse()

  return (
    <div style={{ padding: '20px' }}>
      <div className="my-class"></div>
      <LevelSelect
        value={level}
        onChange={(item) => setLevel($EntityLevel.from(item))}
        options={entityLevels}
      />

      {level != EntityLevel.None ? (
        <>
          <div style={{ margin: '20px 0' }}>
            <span className="label">Name:</span>
            <input
              className="search-data"
              id="searchText"
              placeholder="Enter a search string"
              value={searchText}
              onChange={(e) => setsearchText(e.target.value)}
            />
          </div>

          <div style={{ maxHeight: '80vh' }}>
            <LevelTable level={level} searchText={searchText} />
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  )
}

export default Admin
